const getConversationMessages = (conversation = null) =>
  Array.isArray(conversation?.messages) ? conversation.messages : [];

const getConversationMessageCount = (conversation = null) => {
  const count = conversation?.messages_count ?? conversation?.messagesCount;
  const numericCount = Number(count);

  return Number.isFinite(numericCount) ? numericCount : null;
};

const isConversationListPreviewMessage = (message = null) =>
  message?.source === 'conversation_list';

const isConversationMessageHydrationPending = (message = null) =>
  isConversationListPreviewMessage(message) ||
  message?.hydrationPending === true;

const hasLoadedRenderableMessage = (conversation = null) =>
  getConversationMessages(conversation).some(
    (message) => !isConversationMessageHydrationPending(message)
  );

const conversationCanBeTreatedAsEmpty = (conversation = null) => {
  const messageCount = getConversationMessageCount(conversation);

  if (messageCount !== null) {
    return messageCount === 0;
  }

  return (
    conversation?.allMessagesLoaded === true &&
    getConversationMessages(conversation).length === 0
  );
};

const hasLoadedMessagesForActiveConversation = (conversation = null) => {
  if (conversation?.dataFetched !== true) {
    return false;
  }

  if (hasLoadedRenderableMessage(conversation)) {
    return true;
  }

  return conversationCanBeTreatedAsEmpty(conversation);
};

const shouldUseCachedMessagesForActiveConversation = ({
  cachedMessages = null,
  conversation = null,
} = {}) => {
  if (!cachedMessages || !Array.isArray(cachedMessages.payload)) {
    return false;
  }

  if (cachedMessages.payload.length > 0) {
    return true;
  }

  return getConversationMessageCount(conversation) === 0;
};

const shouldMarkMessagesFetchedAfterPayload = ({
  conversation = null,
  payload = [],
} = {}) => {
  if (Array.isArray(payload) && payload.length > 0) {
    return true;
  }

  return getConversationMessageCount(conversation) === 0;
};

const toNumericMessageId = (id) => {
  if (id === null || id === undefined || id === '') {
    return null;
  }
  const numericId = Number(id);
  return Number.isFinite(numericId) ? numericId : null;
};

const getNewestMessageId = (messages = [], { includePreview = true } = {}) =>
  (Array.isArray(messages) ? messages : []).reduce((newestId, message) => {
    if (!includePreview && isConversationMessageHydrationPending(message)) {
      return newestId;
    }
    const messageId = toNumericMessageId(message?.id);
    if (messageId === null) {
      return newestId;
    }
    return newestId === null || messageId > newestId ? messageId : newestId;
  }, null);

const getNewestRenderableMessageId = (conversation = null) =>
  getNewestMessageId(getConversationMessages(conversation), {
    includePreview: false,
  });

const getNewestKnownMessageId = (conversation = null) =>
  getNewestMessageId(getConversationMessages(conversation));

const getNewestConversationListPreviewMessageId = (conversation = null) =>
  getNewestMessageId(
    getConversationMessages(conversation).filter(
      isConversationListPreviewMessage
    )
  );

const getHydrationPendingMessageIds = (conversation = null) => {
  const messages = getConversationMessages(conversation);
  const hydratedMessageIds = new Set(
    messages
      .filter((message) => !isConversationMessageHydrationPending(message))
      .map((message) => toNumericMessageId(message?.id))
      .filter((messageId) => messageId !== null)
  );

  return messages
    .filter(isConversationMessageHydrationPending)
    .map((message) => toNumericMessageId(message?.id))
    .filter(
      (messageId) => messageId !== null && !hydratedMessageIds.has(messageId)
    );
};

const getConversationTailRevalidationCursor = (conversation = null) => {
  const pendingMessageIds = getHydrationPendingMessageIds(conversation);
  if (!pendingMessageIds.length) {
    return getNewestRenderableMessageId(conversation);
  }

  const oldestPendingMessageId = Math.min(...pendingMessageIds);
  const hydratedIdsBeforeGap = getConversationMessages(conversation)
    .filter((message) => !isConversationMessageHydrationPending(message))
    .map((message) => toNumericMessageId(message?.id))
    .filter(
      (messageId) => messageId !== null && messageId < oldestPendingMessageId
    );

  return hydratedIdsBeforeGap.length
    ? Math.max(...hydratedIdsBeforeGap)
    : oldestPendingMessageId - 1;
};

// True quando lo store conosce un messaggio (tipicamente il preview della
// lista) più nuovo dell'ultimo messaggio completo: il thread mostrato è
// integro ma la coda va riallineata con un fetch `after` leggero.
const conversationNeedsTailRevalidation = (conversation = null) => {
  if (getHydrationPendingMessageIds(conversation).length > 0) {
    return true;
  }

  const newestRenderableId = getNewestRenderableMessageId(conversation);
  if (newestRenderableId === null) {
    return false;
  }

  const newestKnownId = getNewestKnownMessageId(conversation);
  return newestKnownId !== null && newestKnownId > newestRenderableId;
};

const getMessageIdentityKey = (message = null) => {
  const messageId = message?.id;
  return messageId === null || messageId === undefined
    ? null
    : String(messageId);
};

const toMessageTimestamp = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return numericValue;
  }

  const parsedValue = Date.parse(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const compareConversationMessagesChronologically = (first, second) => {
  const firstTimestamp = toMessageTimestamp(first?.created_at);
  const secondTimestamp = toMessageTimestamp(second?.created_at);
  if (
    firstTimestamp !== null &&
    secondTimestamp !== null &&
    firstTimestamp !== secondTimestamp
  ) {
    return firstTimestamp - secondTimestamp;
  }

  const firstId = toNumericMessageId(first?.id);
  const secondId = toNumericMessageId(second?.id);
  if (firstId !== null && secondId !== null && firstId !== secondId) {
    return firstId - secondId;
  }

  return 0;
};

const sortConversationMessagesChronologically = (messages = []) =>
  (Array.isArray(messages) ? messages : [])
    .map((message, index) => ({ index, message }))
    .sort(
      (first, second) =>
        compareConversationMessagesChronologically(
          first.message,
          second.message
        ) || first.index - second.index
    )
    .map(({ message }) => message);

const getNewestConversationListPreview = (messages = []) =>
  (Array.isArray(messages) ? messages : [])
    .filter(isConversationListPreviewMessage)
    .reduce((newestPreview, preview) => {
      if (!newestPreview) {
        return preview;
      }
      return compareConversationMessagesChronologically(
        newestPreview,
        preview
      ) <= 0
        ? preview
        : newestPreview;
    }, null);

const getNewestHydratedMessage = (messages = []) =>
  (Array.isArray(messages) ? messages : [])
    .filter((message) => !isConversationMessageHydrationPending(message))
    .reduce((newestMessage, message) => {
      if (!newestMessage) return message;
      return compareConversationMessagesChronologically(
        newestMessage,
        message
      ) <= 0
        ? message
        : newestMessage;
    }, null);

const isPreviewNewerThanHydratedMessages = (preview, messages = []) => {
  if (!preview) return false;
  const newestHydratedMessage = getNewestHydratedMessage(messages);
  return (
    !newestHydratedMessage ||
    compareConversationMessagesChronologically(newestHydratedMessage, preview) <
      0
  );
};

const mergeConversationMessagesCanonically = (
  previousMessages = [],
  detailMessages = []
) => {
  const messagesById = new Map();
  const messagesWithoutId = [];

  const addMessage = (message) => {
    const identityKey = getMessageIdentityKey(message);
    if (identityKey === null) {
      messagesWithoutId.push(message);
      return;
    }
    messagesById.set(identityKey, message);
  };

  (Array.isArray(previousMessages) ? previousMessages : []).forEach(addMessage);
  // I dettagli API devono sostituire preview/realtime omonimi anche quando il
  // tipo dell'id cambia tra stringa e numero.
  (Array.isArray(detailMessages) ? detailMessages : []).forEach(addMessage);

  const mergedMessages = [...messagesById.values(), ...messagesWithoutId];
  const fullMessages = mergedMessages.filter(
    (message) => !isConversationListPreviewMessage(message)
  );
  const fullMessageIds = new Set(
    fullMessages.map(getMessageIdentityKey).filter((id) => id !== null)
  );
  const newestPreview = getNewestConversationListPreview(mergedMessages);
  const newestPreviewId = getMessageIdentityKey(newestPreview);

  if (
    newestPreview &&
    (newestPreviewId === null || !fullMessageIds.has(newestPreviewId)) &&
    isPreviewNewerThanHydratedMessages(newestPreview, fullMessages)
  ) {
    fullMessages.push(newestPreview);
  }

  return sortConversationMessagesChronologically(fullMessages);
};

const CONVERSATION_MESSAGE_MERGE_DIRECTION = Object.freeze({
  AFTER: 'after',
  BEFORE: 'before',
  INITIAL: 'initial',
});

const detailMessageMergeByDirection = Object.freeze({
  [CONVERSATION_MESSAGE_MERGE_DIRECTION.AFTER]:
    mergeConversationMessagesCanonically,
  [CONVERSATION_MESSAGE_MERGE_DIRECTION.BEFORE]:
    mergeConversationMessagesCanonically,
  [CONVERSATION_MESSAGE_MERGE_DIRECTION.INITIAL]:
    mergeConversationMessagesCanonically,
});

// `direction` rende esplicito il contratto del chiamante. Tutti i percorsi
// convergono oggi sul merge canonico perché API-data-wins + sort cronologico
// è corretto sia per pagine `before`, sia per delta `after`, sia per snapshot.
const mergeConversationMessagesForDetailUpdate = (
  previousMessages = [],
  detailMessages = [],
  { direction = CONVERSATION_MESSAGE_MERGE_DIRECTION.BEFORE } = {}
) => {
  const mergeForDirection =
    detailMessageMergeByDirection[direction] ||
    detailMessageMergeByDirection[CONVERSATION_MESSAGE_MERGE_DIRECTION.BEFORE];
  return mergeForDirection(previousMessages, detailMessages);
};

// Merge lista→store per una conversazione non selezionata: il payload della
// lista porta solo il preview dell'ultimo messaggio, quindi non deve mai
// sovrascrivere una storia completa già caricata. I preview sono stato di
// sincronizzazione, non storia: ne conserviamo al massimo uno, il più recente.
const mergeConversationMessagesForListUpdate = (
  prev = null,
  incoming = null
) => {
  const merged = { ...(prev || {}), ...(incoming || {}) };
  const previousMessages = getConversationMessages(prev);
  const incomingMessages = Array.isArray(incoming?.messages)
    ? incoming.messages
    : [];
  const detailMessages = mergeConversationMessagesForDetailUpdate(
    previousMessages.filter(
      (message) => !isConversationListPreviewMessage(message)
    ),
    incomingMessages.filter(
      (message) => !isConversationListPreviewMessage(message)
    )
  );
  const detailMessageIds = new Set(
    detailMessages.map(getMessageIdentityKey).filter((id) => id !== null)
  );
  const newestPreview = getNewestConversationListPreview([
    ...previousMessages,
    ...incomingMessages,
  ]);
  const newestPreviewId = getMessageIdentityKey(newestPreview);

  merged.messages = sortConversationMessagesChronologically([
    ...detailMessages,
    ...(newestPreview &&
    (newestPreviewId === null || !detailMessageIds.has(newestPreviewId)) &&
    isPreviewNewerThanHydratedMessages(newestPreview, detailMessages)
      ? [newestPreview]
      : []),
  ]);
  return merged;
};

export {
  conversationCanBeTreatedAsEmpty,
  conversationNeedsTailRevalidation,
  getConversationTailRevalidationCursor,
  getConversationMessageCount,
  getNewestConversationListPreviewMessageId,
  getNewestKnownMessageId,
  getNewestRenderableMessageId,
  hasLoadedMessagesForActiveConversation,
  hasLoadedRenderableMessage,
  isConversationMessageHydrationPending,
  isConversationListPreviewMessage,
  CONVERSATION_MESSAGE_MERGE_DIRECTION,
  mergeConversationMessagesForDetailUpdate,
  mergeConversationMessagesForListUpdate,
  shouldMarkMessagesFetchedAfterPayload,
  shouldUseCachedMessagesForActiveConversation,
};
